import {
	INTEGRATION_APP_LISTING_PROPOSAL_STATUSES,
	type IntegrationAppListingProposalStatus,
} from "@repo/shared-primitives"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import * as React from "react"
import { PageHeader, Toolbar } from "@/components/layout"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { RequestsTabs } from "@/features/api-requests-admin/RequestsTabs"
import { SegmentedFilter } from "@/features/api-requests-admin/SegmentedFilter"
import { ListingProposalsGrid } from "@/features/integration-app-listing-proposals/ListingProposalsGrid"
import { LISTING_PROPOSAL_STATUS_LABELS } from "@/features/integration-app-listing-proposals/listing-proposal-labels"
import type { ListingProposalsAdminInput } from "@/features/integration-app-listing-proposals/listing-proposals-datasource"
import type { AdminListingProposalSummary } from "@/types/marketplace.types"

/** `all` lifts the status filter; an absent `status` means the pending backlog. */
const STATUS_FILTERS = [
	...INTEGRATION_APP_LISTING_PROPOSAL_STATUSES,
	"all",
] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

const DEFAULT_STATUS: StatusFilter = "pending"
const SORT_ORDERS = ["asc", "desc"] as const
type SortOrder = (typeof SORT_ORDERS)[number]
const DEFAULT_SORT: SortOrder = "asc"

export const Route = createFileRoute("/integration-apps/listing-requests/")({
	component: ListingRequestsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		search: typeof search.search === "string" ? search.search : undefined,
		status: normalizeStatus(search.status),
		sort: normalizeSort(search.sort),
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

/** Anything not a known status is dropped rather than forwarded to the API. */
function normalizeStatus(raw: unknown): StatusFilter | undefined {
	return STATUS_FILTERS.find((s) => s === raw)
}

function normalizeSort(raw: unknown): SortOrder | undefined {
	return SORT_ORDERS.find((s) => s === raw)
}

const STATUS_CHIPS: IntegrationAppListingProposalStatus[] = [
	"pending",
	"accepted",
	"partially_accepted",
	"rejected",
	"withdrawn",
]

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
	...STATUS_CHIPS.map((chip) => ({
		value: chip,
		label: LISTING_PROPOSAL_STATUS_LABELS[chip],
	})),
	{ value: "all", label: "All" },
]

/** Backlog of owners' proposed listing changes, pending first, oldest first. */
function ListingRequestsPage() {
	const searchParams = useSearch({
		from: "/integration-apps/listing-requests/",
	})
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const status = searchParams.status ?? DEFAULT_STATUS
	const sortOrder = searchParams.sort ?? DEFAULT_SORT

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/integration-apps/listing-requests",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) =>
			updateFilters({ search: value || undefined, page: undefined }),
		[updateFilters],
	)

	const selectStatus = React.useCallback(
		(next: StatusFilter) => {
			updateFilters({
				status: next === DEFAULT_STATUS ? undefined : next,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const input = React.useMemo<ListingProposalsAdminInput>(
		() => ({ search: searchParams.search, status, sortOrder }),
		[searchParams.search, status, sortOrder],
	)

	const handlePageChange = React.useCallback(
		(nextIndex: number) =>
			updateFilters({ page: nextIndex === 0 ? undefined : nextIndex + 1 }),
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(order: SortOrder) =>
			updateFilters({ sort: order === DEFAULT_SORT ? undefined : order }),
		[updateFilters],
	)

	const handleRowClicked = React.useCallback(
		(proposal: AdminListingProposalSummary) =>
			navigate({
				to: "/integration-apps/listing-requests/$proposalId",
				params: { proposalId: proposal.id },
			}),
		[navigate],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-4 p-6">
			<PageHeader
				title="Requests"
				description="Changes app owners proposed to their directory listing; nothing changes on the live app until you accept"
			/>
			<RequestsTabs />

			<Toolbar>
				<DebouncedSearchInput
					defaultValue={searchParams.search ?? ""}
					onDebouncedChange={handleDebouncedSearch}
					placeholder="Search by app name or slug…"
				/>
				<SegmentedFilter
					label="Status"
					options={STATUS_OPTIONS}
					value={status}
					onChange={selectStatus}
				/>
			</Toolbar>

			<ListingProposalsGrid
				input={input}
				pageIndex={pageIndex}
				onPageChange={handlePageChange}
				onSortChange={handleSortChange}
				onRowClicked={handleRowClicked}
			/>
		</div>
	)
}
